import React, { useState } from 'react'
import { useAuthContext } from '../../contexts/AuthContext'
import { PaymentService } from '../../services/PaymentService'
import { getStripePriceId } from '../../config/stripe'
import { useToast } from '../../contexts/ToastContext'

const PricingPage: React.FC = () => {
    const { user, userProfile, updateUserLocation } = useAuthContext()
    const toast = useToast()
    const [loading, setLoading] = useState(false)
    const [interval, setInterval] = useState<'month' | 'year'>('month')
    // Forced to Argentina for MVP
    const [selectedCountry] = useState<string>('AR')

    // Effect for updating location removed for MVP

    /* 
    const handleCountryChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const country = e.target.value
        setSelectedCountry(country)
        if (user) {
            await updateUserLocation(country)
        }
    }
    */

    const isPremium = userProfile?.subscriptionPlan === 'premium'

    const handleSubscribe = async () => {
        if (!user) {
            toast.error('Error', 'Debes iniciar sesión para suscribirte')
            return
        }

        setLoading(true)
        try {
            // Always MercadoPago for Argentina
            if (selectedCountry === 'AR') {
                // Use the new subscription method (preapproval)
                await PaymentService.startMercadoPagoSubscription(interval)
            } else {
                // Stripe Logic (Disabled for now)
                /*
                const priceId = getStripePriceId(interval)
                await PaymentService.startStripeCheckout(priceId, user.uid)
                */
            }
        } catch (error) {
            console.error('Subscription error:', error)
            toast.error('Error', 'No se pudo iniciar el proceso de pago. Intenta más tarde.')
        } finally {
            setLoading(false)
        }
    }

    const handleManageSubscription = async () => {
        setLoading(true)
        try {
            // For Argentina, cancel MercadoPago subscription
            if (selectedCountry === 'AR') {
                if (window.confirm('¿Estás seguro de que deseas cancelar tu suscripción Premium? Perderás acceso a todas las funciones premium.')) {
                    await PaymentService.cancelMercadoPagoSubscription()
                    toast.success('Éxito', 'Tu suscripción ha sido cancelada exitosamente.')
                    // Reload page to reflect changes
                    window.location.reload()
                }
            } else {
                // For other countries, use Stripe portal
                await PaymentService.redirectToCustomerPortal()
            }
        } catch (error) {
            console.error('Subscription management error:', error)
            toast.error('Error', 'No se pudo procesar la solicitud. Intenta más tarde.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center mb-12">
                <h2 className="text-base font-semibold text-primary uppercase tracking-wide">Planes y Precios</h2>
                <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                    Elige el plan perfecto para tu cuidado
                </p>
                <p className="mt-4 max-w-2xl text-xl text-gray-500 dark:text-gray-400 mx-auto">
                    Gestiona la salud de tus seres queridos con tranquilidad y sin límites.
                </p>
            </div>

            <div className="flex flex-col items-center mb-8 gap-4">
                {/* Country Selector Hidden for MVP
                <div className="flex items-center gap-2">
                    <label htmlFor="country-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        País de facturación:
                    </label>
                    <select
                        id="country-select"
                        value={selectedCountry}
                        onChange={() => {}} 
                        disabled
                        className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    >
                        <option value="US">Estados Unidos / Global</option>
                        <option value="AR">Argentina</option>
                        <option value="ES">España</option>
                        <option value="MX">México</option>
                        <option value="CO">Colombia</option>
                    </select>
                </div>
                */}


                <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-lg inline-flex">
                    <button
                        onClick={() => setInterval('month')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${interval === 'month'
                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                            }`}
                    >
                        Mensual
                    </button>
                    <button
                        onClick={() => setInterval('year')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${interval === 'year'
                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                            }`}
                    >
                        Anual (Ahorra 20%)
                    </button>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {/* Free Plan */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 flex flex-col">
                    <div className="mb-4">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">Básico</h3>
                        <p className="text-gray-500 dark:text-gray-400">Para comenzar a organizar el cuidado.</p>
                    </div>
                    <div className="mb-6">
                        <span className="text-4xl font-extrabold text-gray-900 dark:text-white">$0</span>
                        <span className="text-gray-500 dark:text-gray-400">/siempre</span>
                    </div>
                    <ul className="space-y-4 mb-8 flex-1">
                        <li className="flex items-center text-gray-600 dark:text-gray-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                            1 Caso de Cuidado
                        </li>
                        <li className="flex items-center text-gray-600 dark:text-gray-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                            Max 2 Miembros
                        </li>
                        <li className="flex items-center text-gray-600 dark:text-gray-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                            Max 5 Medicamentos
                        </li>
                        <li className="flex items-center text-gray-600 dark:text-gray-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                            Recordatorios básicos
                        </li>
                    </ul>
                    <button
                        disabled
                        className="w-full py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-400 font-semibold rounded-lg cursor-not-allowed"
                    >
                        Plan actual
                    </button>
                </div>

                {/* Premium Plan */}
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border-2 border-primary p-8 flex flex-col transform md:-translate-y-4">
                    <div className="absolute top-0 right-0 -mt-4 mr-4 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                        Recomendado
                    </div>
                    <div className="mb-4">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">Premium</h3>
                        <p className="text-gray-500 dark:text-gray-400">Cuidado sin límites para toda la familia.</p>
                    </div>
                    <div className="mb-6">
                        <div className="flex items-baseline">
                            <span className="text-4xl font-extrabold text-gray-900 dark:text-white">
                                {selectedCountry === 'AR'
                                    ? (interval === 'month' ? '$5.000' : '$50.000') // Example ARS prices
                                    : (interval === 'month' ? '$5' : '$48') // USD prices
                                }
                            </span>
                            <span className="text-gray-500 dark:text-gray-400 ml-1">
                                {selectedCountry === 'AR' ? 'ARS' : 'USD'} /{interval === 'month' ? 'mes' : 'año'}
                            </span>
                        </div>
                        {interval === 'year' && (
                            <p className="text-green-600 text-sm mt-1">Ahorras $12 al año</p>
                        )}
                    </div>
                    <ul className="space-y-4 mb-8 flex-1">
                        <li className="flex items-center text-gray-600 dark:text-gray-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                            <strong>Casos ilimitados</strong>
                        </li>
                        <li className="flex items-center text-gray-600 dark:text-gray-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                            <strong>Miembros ilimitados</strong>
                        </li>
                        <li className="flex items-center text-gray-600 dark:text-gray-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                            <strong>Medicamentos ilimitados</strong>
                        </li>
                        <li className="flex items-center text-gray-600 dark:text-gray-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                            Prioridad en soporte
                        </li>
                        <li className="flex items-center text-gray-600 dark:text-gray-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                            Avatares premium
                        </li>
                    </ul>

                    {isPremium ? (
                        <button
                            onClick={handleManageSubscription}
                            disabled={loading}
                            className="w-full py-3 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold rounded-lg transition-colors"
                        >
                            {loading ? 'Cargando...' : 'Administrar Suscripción'}
                        </button>
                    ) : (
                        <button
                            onClick={handleSubscribe}
                            disabled={loading}
                            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-md transition-all transform hover:scale-[1.02]"
                        >
                            {loading ? 'Procesando...' : 'Mejorar ahora 🚀'}
                        </button>
                    )}
                </div>
            </div>

            <div className="mt-12 text-center text-gray-500 dark:text-gray-400 text-sm">
                <p>
                    {selectedCountry === 'AR' 
                        ? 'Pagos procesados de forma segura por Mercado Pago. Puedes cancelar en cualquier momento.'
                        : 'Pagos procesados de forma segura por Stripe. Puedes cancelar en cualquier momento.'
                    }
                </p>
            </div>
        </div>
    )
}

export default PricingPage
